/**
 * What stands in the table's box when there are no rows, and what stands in the panel's column
 * beside it.
 *
 * Five causes, and `model/exerciseListStatus` is the one that decides which. Of the five, ONE is a
 * shape and four are sentences: the wait is drawn as the table it is about to be, and the other
 * four are said in words, because an error, an empty catalogue, an empty `Twoje` and a filter that
 * matched nothing are all things to SAY rather than shapes to draw.
 *
 * Each sentence names its own way out — retry, reload, add an exercise, clear the filter. A notice
 * with no way out is the version that reads as a bug, and writing them side by side as data is what
 * makes forgetting one visible.
 */

import { useTranslation } from 'react-i18next';

import { EmptyState } from '~/shared/ui/EmptyState';
import { DoneMarkIcon, DumbbellMarkIcon } from '~/shared/ui/EmptyStateIcons';
import ember from '~/shared/ui/emberButton.module.css';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import box from '~/shared/ui/listState.module.css';
import styles from './ExerciseListStates.module.css';

/** Eight rows, at the row's own height — the wait is the shape of the answer. */
const SKELETON_ROWS = 8;

export function ExerciseListSkeleton({ skeleton }: { readonly skeleton: boolean }) {
  const { t } = useTranslation();
  return (
    <div className={styles.skeletonList} aria-busy="true">
      {/* The word a listener needs; the bars are decorative `<i>`s with no name of their own. */}
      <p className={hidden.srOnly}>{t('web:exerciseLibrary.states.loading')}</p>
      {Array.from({ length: SKELETON_ROWS }, (_, index) => (
        <div key={index} className={styles.skeletonRow}>
          {/* The gate hides the BARS, never the box: every row keeps its 52px whether or not it is
            * drawing anything, so the catalogue arriving changes ink and not geometry. */}
          {skeleton && (
            <>
              <i className={`${String(skel.bar)} ${String(styles.skelStill)}`} />
              <i className={`${String(skel.bar)} ${String(styles.skelName)}`} />
              <i className={`${String(skel.bar)} ${String(styles.skelType)}`} />
              <i className={`${String(skel.bar)} ${String(styles.skelPills)}`} />
              <i className={`${String(skel.bar)} ${String(styles.skelThumbs)}`} />
              <i className={`${String(skel.bar)} ${String(styles.skelAuthor)}`} />
            </>
          )}
        </div>
      ))}
    </div>
  );
}

export function ExerciseListError({ onRetry }: { readonly onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div className={box.state}>
      <EmptyState
        mark={<DumbbellMarkIcon />}
        tone="neutral"
        title={t('web:exerciseLibrary.states.errorTitle')}
        hint={t('web:exerciseLibrary.states.errorMessage')}
      />
      <div className={box.actions}>
        <button type="button" className={box.ghost} onClick={onRetry}>
          {t('web:exerciseLibrary.states.retry')}
        </button>
      </div>
    </div>
  );
}

/**
 * The whole catalogue is empty.
 *
 * On this screen that is not an ordinary state: the library is 477 rows of shared content and there
 * is no way for a trainer to have none of it. So the tone is neutral and the way out is a retry,
 * not "add your first exercise" — which is what `emptyScope` below says, and putting that sentence
 * under a library that failed to seed would be telling the trainer to fix the server.
 */
export function ExerciseCatalogueEmpty({ onRetry }: { readonly onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div className={box.state}>
      <EmptyState
        mark={<DumbbellMarkIcon />}
        tone="neutral"
        title={t('web:exerciseLibrary.emptyCatalogue.title')}
        hint={t('web:exerciseLibrary.emptyCatalogue.message')}
      />
      <div className={box.actions}>
        <button type="button" className={box.ghost} onClick={onRetry}>
          {t('web:exerciseLibrary.states.retry')}
        </button>
      </div>
    </div>
  );
}

/**
 * `Twoje` with nothing in it — the ordinary first visit.
 *
 * The way out is the creator — „Nowe ćwiczenie", the same act as the header's `+` — which is what a
 * trainer with no movements of their own came to this list to do.
 */
export function ExerciseScopeEmpty({ onCreate }: { readonly onCreate: () => void }) {
  const { t } = useTranslation();
  return (
    <div className={box.state}>
      <EmptyState
        mark={<DumbbellMarkIcon />}
        tone="action"
        title={t('web:exerciseLibrary.emptyScope.title')}
        hint={t('web:exerciseLibrary.emptyScope.message')}
      />
      <div className={box.actions}>
        <button
          type="button"
          className={`${String(ember.ember)} ${String(box.primary)}`}
          onClick={onCreate}
        >
          {t('web:exerciseLibrary.emptyScope.action')}
        </button>
      </div>
    </div>
  );
}

interface NoResultsProps {
  readonly term: string;
  readonly facets: boolean;
  /**
   * The search's other-language names could not be read. A typed term then matched in one language
   * only, so „nothing matched" may be wrong about it — one more sentence says the check did not run.
   */
  readonly otherLanguageFailed: boolean;
  readonly onClear: () => void;
}

export function ExerciseNoResults({ term, facets, otherLanguageFailed, onClear }: NoResultsProps) {
  const { t } = useTranslation();
  const hintKey = term !== '' && facets ? 'searchAndFilters' : term !== '' ? 'search' : 'filters';
  const hint = t(`web:exerciseLibrary.noResults.${hintKey}`, { term });
  return (
    <div className={box.state}>
      <EmptyState
        mark={<DumbbellMarkIcon />}
        tone="neutral"
        title={t('web:exerciseLibrary.noResults.title')}
        hint={
          term !== '' && otherLanguageFailed
            ? `${hint} ${t('web:exerciseLibrary.language.otherFailed')}`
            : hint
        }
      />
      <div className={box.actions}>
        <button type="button" className={box.ghost} onClick={onClear}>
          {t('web:exerciseLibrary.noResults.clear')}
        </button>
      </div>
    </div>
  );
}

/**
 * „Do uzupełnienia" pressed, and nothing in the list carries the mark (board D, R17): every movement
 * added from Buddy's proposals has been checked. Good news, said as such, and the way out lets go of
 * the toggle.
 */
export function ExerciseReviewDone({ onShowAll }: { readonly onShowAll: () => void }) {
  const { t } = useTranslation();
  return (
    <div className={box.state}>
      <EmptyState
        mark={<DoneMarkIcon />}
        tone="positive"
        title={t('web:exerciseLibrary.review.allChecked')}
        hint={t('web:exerciseLibrary.review.allCheckedHint')}
      />
      <div className={box.actions}>
        <button type="button" className={box.ghost} onClick={onShowAll}>
          {t('web:exerciseLibrary.review.showAll')}
        </button>
      </div>
    </div>
  );
}
